import { useState } from 'react';
import {
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { usePublicStats } from '../hooks/usePublicStats';
import { useMonthlyTrend } from '../hooks/useMonthlyTrend';
import { SCHOOL_FEES, TOTAL_SCHOOL_FEE, DISCOUNT_CATEGORY } from '../../../shared/constant/schoolFees';
import { compareByGradeOrder } from '../../../shared/utils/sortByGrade';
import { monthNames, schoolYearLabel } from '../../../shared/utils/reportingPeriod';
import { useReportingPeriod } from '../../../shared/hooks/useReportingPeriod';
import ReportingPeriodBar from '../../../shared/components/ReportingPeriodBar';
import KpiCard, { kpiTones, signTone } from '../../../shared/components/KpiCard';
import CategoryAdjustments from '../../../shared/components/CategoryAdjustments';
import { peso, pesoWhole } from '../../../shared/utils/formatCurrency';
import MonthlyTrendChart from '../../../shared/components/MonthlyTrendChart';
import PublicLayout from './PublicLayout';

const statConfig = [
  { key: 'totalCollected', topLabel: 'TOTAL COLLECTED' },
  { key: 'totalDisbursed', topLabel: 'TOTAL DISBURSED' },
  { key: 'netBalance', topLabel: 'NET BALANCE ON HAND' },
];

// Per-month value each KPI card compares against the previous month.
const trendMetrics = {
  totalCollected: (point) => point.collected,
  totalDisbursed: (point) => point.disbursed,
  netBalance: (point) => point.collected - point.disbursed,
};

// Spending is normal, not an error, so disbursed stays neutral. Net balance is coloured by its sign.
const cardTones = {
  totalCollected: kpiTones.success,
  totalDisbursed: kpiTones.neutral,
};

const gradeLevelSummary = [
  { grade: 'Grade 7', disbursed: 0 },
  { grade: 'Grade 8', disbursed: 0 },
  { grade: 'Grade 9', disbursed: 0 },
  { grade: 'Grade 10', disbursed: 0 },
  { grade: 'Grade 11', disbursed: 0 },
  { grade: 'Grade 12', disbursed: 0 },
];

// Each status pairs a colour with an icon and a text label, so it never relies on colour alone.
const statusConfig = {
  Completed: { icon: CheckCircleIcon, badge: 'bg-green-100 text-green-800', bar: 'bg-success' },
  'On Track': { icon: ArrowTrendingUpIcon, badge: 'bg-blue-100 text-blue-800', bar: 'bg-blue-600' },
  Behind: { icon: ExclamationTriangleIcon, badge: 'bg-amber-100 text-amber-900', bar: 'bg-amber-500' },
  Pending: { icon: ClockIcon, badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400' },
};

const getCollectionStatus = (collected, target) => {
  if (!target) return 'Pending';
  if (collected >= target) return 'Completed';
  if (collected === 0) return 'Pending';
  return collected / target >= 0.5 ? 'On Track' : 'Behind';
};

function StatusBadge({ status }) {
  if (!status) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-500">
        --
      </span>
    );
  }
  const { icon: Icon, badge } = statusConfig[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${badge}`}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {status}
    </span>
  );
}

function PublicDashboard() {
  const period = useReportingPeriod();
  const {
    schoolYear: selectedSchoolYear,
    rangeMode,
    setRangeMode,
    periods,
    label: periodLabel,
  } = period;
  const [sectionFilter, setSectionFilter] = useState('');

  const { stats, loading, updatedAt } = usePublicStats(periods);
  const { series: trendSeries, loading: trendLoading } = useMonthlyTrend(
    selectedSchoolYear === 'all' ? null : selectedSchoolYear
  );
  const { stats: categoryStats, loading: categoryLoading } = usePublicStats(
    periods,
    sectionFilter || null
  );

  const targetByGrade = (grade) => (stats?.studentCountByGrade?.[grade] ?? 0) * TOTAL_SCHOOL_FEE;
  const totalTarget = Object.values(stats?.studentCountByGrade ?? {}).reduce(
    (sum, count) => sum + count * TOTAL_SCHOOL_FEE,
    0
  );

  const totalStudentCount = Object.values(categoryStats?.studentCountByGrade ?? {}).reduce(
    (sum, count) => sum + count,
    0
  );

  const categorySummary = SCHOOL_FEES.map((fee) => {
    const collected = categoryStats?.collectedByCategory?.[fee.category] ?? 0;
    const expected = fee.amount * totalStudentCount;
    const percent = expected > 0 ? Math.min(100, Math.round((collected / expected) * 100)) : 0;
    return { category: fee.category, collected, expected, percent };
  });

  // The listed fees, the discount and anything else add up to the total collected.
  const feesCollected = categorySummary.reduce((sum, c) => sum + c.collected, 0);
  const discountCollected = categoryStats?.collectedByCategory?.[DISCOUNT_CATEGORY] ?? 0;
  const otherCollected =
    Math.round(((categoryStats?.totalCollected ?? 0) - feesCollected - discountCollected) * 100) / 100;
  const categoryExpected = categorySummary.reduce((sum, c) => sum + c.expected, 0);
  const categoryCollected = feesCollected + discountCollected + otherCollected;
  const categorySummaryTotal = {
    collected: categoryCollected,
    expected: categoryExpected,
    percent:
      categoryExpected > 0 ? Math.min(100, Math.max(0, Math.round((categoryCollected / categoryExpected) * 100))) : 0,
  };

  const totalDisbursed = stats?.totalDisbursed ?? 0;
  const netBalance = (stats?.totalCollected ?? 0) - totalDisbursed;

  const statValues = {
    totalCollected: stats?.totalCollected,
    totalDisbursed,
    netBalance,
  };

  const totalCollected = stats?.totalCollected ?? 0;
  const shareOfCollected = (amount) =>
    totalCollected > 0 ? Math.round((amount / totalCollected) * 100) : null;

  const cardProgress = {
    totalCollected: {
      percent: totalTarget > 0 ? Math.round((totalCollected / totalTarget) * 100) : null,
      label: `of ${pesoWhole(totalTarget)} target`,
    },
    totalDisbursed: { percent: shareOfCollected(totalDisbursed), label: 'of funds collected utilized' },
    netBalance: { percent: shareOfCollected(netBalance), label: 'of funds collected on hand' },
  };

  // Trend data: the first entry is the month before the school year, kept only for comparisons.
  const selectedPeriod = rangeMode === 'period' && selectedSchoolYear !== 'all' ? periods[0] : null;
  const selectedTrendIndex = selectedPeriod
    ? trendSeries.findIndex((p) => p.month === selectedPeriod.month && p.year === selectedPeriod.year)
    : -1;
  const previousPoint = selectedTrendIndex > 0 ? trendSeries[selectedTrendIndex - 1] : null;
  const previousLabel = previousPoint ? monthNames[previousPoint.month - 1].slice(0, 3) : '';

  // Chart data. The trend series starts with the month before the school year, kept only for comparisons.
  const trendPoints = trendSeries.filter(
    (point) => !(point.month === 5 && point.year === selectedSchoolYear)
  );

  const totalEnrolled = Object.values(stats?.studentCountByGrade ?? {}).reduce((sum, n) => sum + n, 0);
  const paidStudentCount = stats?.paidStudentCount;

  const hasNoCollections = !loading && stats != null && stats.totalCollected === 0;
  const canWidenPeriod = selectedSchoolYear !== 'all' && rangeMode === 'period';

  return (
    <PublicLayout>
      <ReportingPeriodBar period={period} loading={loading} updatedAt={updatedAt} />

      {/* Empty state: nothing recorded for the chosen period */}
      {hasNoCollections && (
        <section
          role="status"
          className="w-full flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          <p className="text-base font-medium">
            {selectedSchoolYear === 'all'
              ? 'No collections have been recorded yet.'
              : `No collections recorded for ${periodLabel} yet.`}
          </p>
          {canWidenPeriod && (
            <button
              type="button"
              onClick={() => setRangeMode('ytd')}
              className="rounded-md border border-amber-400 bg-white px-3 py-2 text-base font-medium text-amber-900 hover:bg-amber-100"
            >
              View year to date
            </button>
          )}
        </section>
      )}

      {/* Stats + grade level summary */}
      <div className="w-full space-y-4">
        {/* Public stats: enrolled students / IDs claimed */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {statConfig.map(({ key, topLabel }) => {
            const metric = trendMetrics[key];
            const tone = key === 'netBalance' ? signTone(netBalance) : cardTones[key];
            const { percent, label } = cardProgress[key];
            const currentPoint = selectedTrendIndex >= 0 ? trendSeries[selectedTrendIndex] : null;

            return (
              <KpiCard
                key={key}
                label={topLabel}
                value={statValues[key] != null ? peso(statValues[key]) : null}
                tone={tone}
                loading={loading}
                caption={percent == null ? '--' : `${percent}% ${label}`}
                percent={percent}
                change={
                  currentPoint && previousPoint
                    ? {
                        current: metric(currentPoint),
                        previous: metric(previousPoint),
                        previousLabel,
                        neutral: key === 'totalDisbursed',
                      }
                    : null
                }
              >
                {key === 'totalCollected' && paidStudentCount != null && totalEnrolled > 0 && (
                  <p className="text-sm text-gray-700">
                    {paidStudentCount.toLocaleString()} of {totalEnrolled.toLocaleString()} students paid
                  </p>
                )}
              </KpiCard>
            );
          })}
        </section>

        <MonthlyTrendChart
          points={trendPoints}
          loading={trendLoading}
          schoolYearText={selectedSchoolYear === 'all' ? null : schoolYearLabel(selectedSchoolYear)}
        />

        {/* Grade level collection summary: table on desktop, cards on mobile */}
        <section className="w-full bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="-mx-4 -mt-4 mb-3 rounded-t-lg bg-navy-light px-4 py-2 text-md lg:text-lg font-semibold text-white">Grade Level Collection Summary</h2>

          {/* Desktop/tablet table, hidden on mobile */}
          <div className="hidden sm:block -mx-4 overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-sm text-navy uppercase">
                  <th className="py-2 pl-4 pr-4 font-semibold whitespace-nowrap">Grade Level</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Collected</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Progress</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Disbursed</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Balance</th>
                  <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gradeLevelSummary.map(({ grade, disbursed }) => {
                  const collected = loading ? 0 : stats?.collectedByGrade?.[grade] ?? 0;
                  const target = targetByGrade(grade);
                  const status = loading ? null : getCollectionStatus(collected, target);
                  const percent = target ? Math.round((collected / target) * 100) : 0;
                  return (
                    <tr key={grade} className="hover:bg-gold/10 active:bg-gold/20 transition-colors">
                      <td className="py-3 pl-4 pr-4 font-medium text-gray-900 whitespace-nowrap">{grade}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">
                        {loading ? '--' : peso(collected)}
                      </td>
                      <td className="py-3 pr-4 min-w-[140px]">
                        {loading ? (
                          <span className="text-gray-700">--</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${statusConfig[status]?.bar ?? 'bg-gray-300'}`}
                                style={{ width: `${Math.min(100, percent)}%` }}
                              />
                            </div>
                            <span className="text-gray-700 text-sm font-medium whitespace-nowrap">{percent}%</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{peso(disbursed)}</td>
                      <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">
                        {loading ? '--' : peso(collected - disbursed)}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <StatusBadge status={status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list, hidden on sm and up */}
          <div className="sm:hidden space-y-3">
            {gradeLevelSummary.map(({ grade, disbursed }) => {
              const collected = loading ? 0 : stats?.collectedByGrade?.[grade] ?? 0;
              const target = targetByGrade(grade);
              const status = loading ? null : getCollectionStatus(collected, target);
              const percent = target ? Math.round((collected / target) * 100) : 0;
              return (
                <div
                  key={grade}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gold/10 active:bg-gold/20 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-gray-900">{grade}</p>
                    <StatusBadge status={status} />
                  </div>
                  <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                    <p className="text-gray-600">Collected</p>
                    <p className="text-right text-gray-700 font-bold">{loading ? '--' : peso(collected)}</p>
                    <p className="text-gray-600">Progress</p>
                    <div className="flex items-center justify-end gap-2">
                      {loading ? (
                        <span className="text-gray-700 font-bold">--</span>
                      ) : (
                        <>
                          <div className="w-16 h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${statusConfig[status]?.bar ?? 'bg-gray-300'}`}
                              style={{ width: `${Math.min(100, percent)}%` }}
                            />
                          </div>
                          <span className="text-right text-gray-700 font-bold">{percent}%</span>
                        </>
                      )}
                    </div>
                    <p className="text-gray-600">Disbursed</p>
                    <p className="text-right text-gray-700 font-bold">{peso(disbursed)}</p>
                    <p className="text-gray-600">Balance</p>
                    <p className="text-right text-gray-700 font-bold">
                      {loading ? '--' : peso(collected - disbursed)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Payment categories summary */}
        <section className="w-full bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="-mx-4 -mt-4 mb-3 rounded-t-lg bg-navy-light px-4 py-2 text-md lg:text-lg font-semibold text-white">
            Payment Categories Summary
          </h2>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="flex-1 min-w-[45%] sm:min-w-[140px] rounded-md border border-gray-300 text-lg text-gray-900 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">All Sections</option>
              {[...(categoryStats?.sections ?? [])].sort(compareByGradeOrder).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setSectionFilter('')}
              disabled={!sectionFilter}
              className="text-lg text-gray-600 border border-gray-300 px-3 py-2 rounded-md hover:text-gray-900 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-600"
            >
              Clear filter
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
            {categorySummary.map(({ category, collected, percent }) => (
              <div key={category}>
                <div className="flex justify-between items-baseline text-sm mb-1 gap-2">
                  <span className="font-medium text-gray-800">{category}</span>
                  <span className="text-gray-700 whitespace-nowrap">
                    {categoryLoading ? '--' : peso(collected)}{' '}
                    <span className="font-medium text-navy">
                      ({categoryLoading ? '--' : `${percent}%`})
                    </span>
                  </span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      percent >= 100 ? 'bg-success' : 'bg-gold'
                    }`}
                    style={{ width: `${categoryLoading ? 0 : percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <CategoryAdjustments discount={discountCollected} other={otherCollected} />

          <div
            className={`mt-5 p-4 rounded-lg ${
              categorySummaryTotal.percent >= 100 ? 'bg-success/10' : 'bg-gold/10'
            }`}
          >
            <div className="flex justify-between items-baseline gap-2 mb-2">
              <span
                className={`text-lg font-bold ${
                  categorySummaryTotal.percent >= 100 ? 'text-success' : 'text-navy'
                }`}
              >
                TOTAL
              </span>
              <span
                className={`text-lg font-bold whitespace-nowrap ${
                  categorySummaryTotal.percent >= 100 ? 'text-success' : 'text-navy'
                }`}
              >
                {categoryLoading ? '--' : peso(categorySummaryTotal.collected)} (
                {categoryLoading ? '--' : `${categorySummaryTotal.percent}%`})
              </span>
            </div>
            <div className="w-full h-3 bg-white/60 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  categorySummaryTotal.percent >= 100 ? 'bg-success' : 'bg-gold'
                }`}
                style={{ width: `${categoryLoading ? 0 : categorySummaryTotal.percent}%` }}
              />
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}

export default PublicDashboard;
